import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { buildMetadata } from "@/lib/metadata";
import { cn } from "@/lib/utils";
import {
  DemandPredictionService,
  type ForecastPeriodType,
  getForecastShadedRegion,
} from "@/services/demand-prediction-service";
import type {
  ActiveShipment,
  DemandDriverTimelineItem,
  DemandForecastPoint,
  DemandPredictionOverviewResponse,
} from "@/services/types/demand-prediction";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Download } from "lucide-react";
import * as React from "react";
import {
  Area,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { useAuth } from "@/providers/auth";
import { ProductService } from "@/services/product-service";

type TabKey = "overview" | "timeline";

function parseForecastPeriod(
  value: unknown,
): ForecastPeriodType {
  return value === "week" ? "week" : "month";
}

export const Route = createFileRoute("/_main/demand-prediction")({
  component: DemandPredictionRoute,
  validateSearch: (search: Record<string, unknown>) => {
    const tab =
      search.tab === "timeline" || search.tab === "overview"
        ? (search.tab as TabKey)
        : ("overview" as const);
    const skuId = typeof search.skuId === "string" ? search.skuId : undefined;
    const period = parseForecastPeriod(search.period);
    const customerId =
      typeof search.customerId === "string" &&
      search.customerId.trim().length > 0
        ? search.customerId.trim()
        : undefined;
    return { tab, skuId, period, customerId };
  },
  head: () => ({
    meta: buildMetadata({
      title: "Demand Prediction",
      description: "Demand planning and prediction",
      keywords: ["demand", "prediction", "planning"],
    }),
  }),
});

function DemandPredictionRoute() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();

  const planningQuery = useQuery({
    queryKey: [
      "demandPlanning",
      search.skuId,
      search.period,
      search.customerId,
    ],
    queryFn: () =>
      DemandPredictionService.fetchPlanningData({
        urlSkuId: search.skuId,
        forecastGranularity: search.period,
        customerId: search.customerId,
      }),
  });

  const { data: allCustomers = [], isPending: isCustomerListPending } =
    useQuery({
      queryKey: ["inventory_customer"],
      queryFn: () => ProductService.listCustomers(),
      staleTime: 60_000,
    });

  const planning = planningQuery.data;
  const overview = planning
    ? DemandPredictionService.toOverview(planning)
    : undefined;
  const timeline = planning
    ? DemandPredictionService.toTimeline(planning)
    : undefined;

  const activeSkuId = overview?.selectedSkuId ?? search.skuId;

  const { user } = useAuth();

  return (
    <div className="space-y-6 p-4 md:p-8">
      {planningQuery.isError && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3 text-destructive text-sm">
          Demand planning data could not be loaded. Ensure your account has
          demand planning enabled and try again.
        </div>
      )}

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <div className="font-semibold text-2xl">Hello, {user?.username}</div>
          <div className="text-muted-foreground text-sm">
            Demand Planning
            <span className="px-2 text-muted-foreground/60">•</span>
            Real-time inventory & procurement intelligence
          </div>
        </div>

        <Button
          variant="outline"
          className="w-fit rounded-xl"
          onClick={() => {
            // Placeholder for future export endpoint
            window.alert("Export coming soon.");
          }}
        >
          <Download className="size-4" />
          Export Data
        </Button>
      </div>

      <Tabs
        value={search.tab}
        onValueChange={(value) => {
          const tab = value === "timeline" ? "timeline" : "overview";
          navigate({ search: (prev) => ({ ...prev, tab }) });
        }}
        className="custom-tabs gap-6"
      >
        <TabsList className="flex bg-transparent">
          <TabsTrigger
            value="overview"
            className="!rounded-none relative border-b-2 border-b-gray-200 px-2 py-2 text-gray-600 data-[state=active]:border-b-2 data-[state=active]:border-b-black data-[state=active]:font-medium data-[state=active]:text-black data-[state=active]:shadow-none md:px-8"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="timeline"
            className="!rounded-none relative border-b-2 border-b-gray-200 px-2 py-2 text-gray-600 data-[state=active]:border-b-2 data-[state=active]:border-b-black data-[state=active]:font-medium data-[state=active]:text-black data-[state=active]:shadow-none md:px-8"
          >
            Timeline
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <TopControls
            skus={overview?.skus ?? []}
            selectedSkuId={activeSkuId}
            onSkuChange={(skuId) => {
              navigate({ search: (prev) => ({ ...prev, skuId }) });
            }}
          />

          <KpiRow
            isLoading={planningQuery.isLoading}
            kpis={overview?.kpis ?? null}
          />

          <Card className="rounded-2xl border-border/60 p-5">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="font-semibold">Volume Forecast</div>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
                <Select
                  value={search.period}
                  onValueChange={(v) => {
                    const period =
                      v === "week" ? ("week" as const) : ("month" as const);
                    navigate({
                      search: (prev) => ({ ...prev, period }),
                    });
                  }}
                >
                  <SelectTrigger className="h-9 w-full rounded-lg sm:w-[160px]">
                    <SelectValue placeholder="Period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">Monthly</SelectItem>
                    <SelectItem value="week">Weekly</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={search.customerId ?? "all"}
                  disabled={isCustomerListPending}
                  onValueChange={(v) => {
                    navigate({
                      search: (prev) => ({
                        ...prev,
                        customerId: v === "all" ? undefined : v,
                      }),
                    });
                  }}
                >
                  <SelectTrigger className="h-9 w-full rounded-lg sm:w-[220px]">
                    <SelectValue
                      placeholder={
                        isCustomerListPending
                          ? "Loading customers…"
                          : "Customer"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All customers</SelectItem>
                    {allCustomers.map((c) => {
                      console.log(c);
                      const label =
                        c.company_name?.trim() ||
                        c.contact_name?.trim() ||
                        "Unknown customer";
                      return (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {label}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-6 lg:flex-row lg:items-stretch">
              <div className="min-w-0 flex-1 lg:min-h-0">
                <div className="h-[260px]">
                  <ForecastChart
                    points={overview?.forecast ?? []}
                    volumeUnit={overview?.kpis?.volumeUnit}
                    isLoading={planningQuery.isLoading}
                  />
                </div>
              </div>

              <aside className="flex w-full shrink-0 flex-col lg:w-[min(100%,288px)] lg:border-border/60 lg:border-l lg:pl-6">
                <div className="font-semibold text-green-900 dark:text-green-100">
                  Market Insights
                </div>
                <div className="mt-3 flex flex-1 flex-col space-y-3">
                  {(overview?.marketInsights ?? []).map((insight, i) => (
                    <div
                      key={`${insight.title}-${i}`}
                      className="rounded-xl border border-border/60 bg-muted/30 p-3"
                    >
                      <div className="font-medium text-sm">{insight.title}</div>
                      <div className="mt-1 text-muted-foreground text-xs leading-relaxed">
                        {insight.body}
                      </div>
                    </div>
                  ))}
                  {!planningQuery.isLoading &&
                    (overview?.marketInsights?.length ?? 0) === 0 && (
                      <div className="text-muted-foreground text-sm">
                        No insights available.
                      </div>
                    )}
                </div>
              </aside>
            </div>
          </Card>

          <Card className="rounded-2xl border-border/60 p-5">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="space-y-1">
                <div className="font-semibold">Active Shipments</div>
                <div className="text-muted-foreground text-xs">
                  Track shipments and delays against stock.
                </div>
              </div>
              <ShipmentFilterPills />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
              {(overview?.activeShipments ?? []).map((s) => (
                <ShipmentCard key={s.id} shipment={s} />
              ))}
              {!planningQuery.isLoading &&
                (overview?.activeShipments?.length ?? 0) === 0 && (
                  <div className="text-muted-foreground text-sm">
                    No active shipments.
                  </div>
                )}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="timeline" className="space-y-6">
          <TopControls
            skus={overview?.skus ?? []}
            selectedSkuId={activeSkuId}
            onSkuChange={(skuId) => {
              navigate({ search: (prev) => ({ ...prev, skuId }) });
            }}
          />

          <Card className="rounded-2xl border-border/60 p-5">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="font-semibold">Active Shipments</div>
              <ShipmentFilterPills />
            </div>
            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
              {(timeline?.activeShipments ?? []).map((s) => (
                <ShipmentCard key={s.id} shipment={s} />
              ))}
              {!planningQuery.isLoading &&
                (timeline?.activeShipments?.length ?? 0) === 0 && (
                  <div className="text-muted-foreground text-sm">
                    No active shipments.
                  </div>
                )}
            </div>
          </Card>

          <Card className="rounded-2xl border-border/60 p-5">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div className="space-y-1">
                <div className="font-semibold">Demand Drivers Timeline</div>
                <div className="text-muted-foreground text-xs">
                  This timeline explains how demand and shipments impact stock
                  over time.
                </div>
              </div>

              <div className="flex items-center gap-2 text-muted-foreground text-xs">
                <span>Today</span>
                <Badge variant="secondary" className="rounded-full">
                  {new Date().toLocaleDateString(undefined, {
                    month: "short",
                    day: "2-digit",
                    year: "numeric",
                  })}
                </Badge>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {(timeline?.demandDriversTimeline ?? []).map((item) => (
                <DemandDriverRow key={item.id} item={item} />
              ))}
              {!planningQuery.isLoading &&
                (timeline?.demandDriversTimeline?.length ?? 0) === 0 && (
                  <div className="text-muted-foreground text-sm">
                    No demand drivers available.
                  </div>
                )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TopControls({
  skus,
  selectedSkuId,
  onSkuChange,
}: {
  skus: DemandPredictionOverviewResponse["skus"];
  selectedSkuId?: string;
  onSkuChange: (skuId: string) => void;
}) {
  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <div className="flex items-center gap-3">
        <div className="text-muted-foreground text-xs">Select SKU</div>
        <Select
          value={selectedSkuId && selectedSkuId.length > 0 ? selectedSkuId : undefined}
          onValueChange={(v) => {
            onSkuChange(v);
          }}
        >
          <SelectTrigger className="h-10 w-[340px] rounded-xl">
            <SelectValue placeholder="Choose an SKU" />
          </SelectTrigger>
          <SelectContent>
            {skus.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function KpiRow({
  kpis,
  isLoading,
}: {
  kpis: DemandPredictionOverviewResponse["kpis"] | null;
  isLoading: boolean;
}) {
  const unit = kpis?.volumeUnit?.trim() || "";
  const volumeSuffix = unit ? ` ${unit}` : "";

  const fmtVol = (v: number | null | undefined) =>
    v != null && Number.isFinite(v)
      ? `${Math.round(v * 10) / 10}${volumeSuffix}`
      : "—";

  const fmtPct = (v: number | null | undefined) =>
    v != null && Number.isFinite(v)
      ? `${Math.round(v * 10) / 10}%`
      : "—";

  const items = [
    {
      label: "Forecast periods",
      value: kpis?.recordsCount != null ? String(kpis.recordsCount) : "—",
    },
    {
      label: "Avg accuracy",
      value: fmtPct(kpis?.predictionAccuracyPct),
    },
    {
      label: "Avg actual volume",
      value: fmtVol(kpis?.avgActualVolume),
    },
    {
      label: "Avg predicted volume",
      value: fmtVol(kpis?.avgPredictedVolume),
    },
  ] as const;

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
      {items.map((i) => (
        <Card key={i.label} className="rounded-2xl border-border/60 p-5">
          <div className="text-muted-foreground text-xs">{i.label}</div>
          <div className="mt-2 font-semibold text-2xl">
            {isLoading ? "…" : i.value}
          </div>
        </Card>
      ))}
    </div>
  );
}

function ForecastChart({
  points,
  volumeUnit,
  isLoading,
}: {
  points: DemandForecastPoint[];
  volumeUnit?: string | null;
  isLoading: boolean;
}) {
  const data = React.useMemo(() => points, [points]);
  const unitLabel = volumeUnit?.trim() || "";

  const config = React.useMemo(
    () => ({
      demandKg: {
        label: unitLabel ? `Actual (${unitLabel})` : "Actual volume",
        color: "hsl(var(--chart-1))",
      },
      forecastKg: {
        label: unitLabel ? `Predicted (${unitLabel})` : "Predicted volume",
        color: "hsl(var(--chart-2))",
      },
      stockKg: { label: "Stock", color: "hsl(var(--chart-3))" },
    }),
    [unitLabel],
  );

  const showStock = React.useMemo(
    () => data.some((p) => p.stockKg != null && Number.isFinite(p.stockKg)),
    [data],
  );

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground text-sm">
        Loading chart…
      </div>
    );
  }

  const { start: forecastStart, end: forecastEnd } =
    getForecastShadedRegion(data);

  return (
    <ChartContainer
      config={config}
      className="h-full w-full"
      id="demand-prediction-forecast"
    >
      <ResponsiveContainer>
        <LineChart
          data={data}
          margin={{ left: 6, right: 10, top: 10, bottom: 6 }}
        >
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="dateISO"
            tickLine={false}
            axisLine={false}
            tickMargin={10}
            minTickGap={24}
            tickFormatter={(v) => {
              const d = new Date(v);
              return d.toLocaleDateString(undefined, {
                month: "short",
                day: "2-digit",
              });
            }}
          />
          <YAxis tickLine={false} axisLine={false} width={44} />
          <ChartTooltip content={<ChartTooltipContent indicator="dot" />} />
          <ChartLegend content={<ChartLegendContent />} />

          {forecastStart && forecastEnd && (
            <ReferenceArea
              x1={forecastStart}
              x2={forecastEnd}
              fill="#fff"
              fillOpacity={0.35}
            />
          )}

          {showStock && (
            <Area
              type="monotone"
              dataKey="stockKg"
              stroke="var(--color-stockKg)"
              fill="var(--color-stockKg)"
              fillOpacity={0.08}
              dot={false}
              isAnimationActive={false}
            />
          )}

          <Line
            type="monotone"
            dataKey="demandKg"
            stroke="var(--color-demandKg)"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="forecastKg"
            stroke="var(--color-forecastKg)"
            strokeDasharray="6 4"
            strokeWidth={2}
            dot={false}
            connectNulls
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
}

function ShipmentFilterPills() {
  const pills = [
    "All",
    "Planned",
    "Delayed",
    "In transit",
    "Received",
  ] as const;
  const [active, setActive] = React.useState<(typeof pills)[number]>("All");

  return (
    <div className="flex flex-wrap items-center gap-2">
      {pills.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => setActive(p)}
          className={cn(
            "rounded-full border border-border/60 bg-background px-3 py-1 text-xs transition-colors",
            active === p && "bg-foreground text-background",
          )}
        >
          {p}
        </button>
      ))}
    </div>
  );
}

function ShipmentCard({ shipment }: { shipment: ActiveShipment }) {
  const status = shipment.status;
  const statusPill =
    status === "planned"
      ? "Planned"
      : status === "in_transit"
        ? "In transit"
        : status === "delayed"
          ? "Delayed"
          : "Received";

  return (
    <div className="rounded-2xl border border-border/60 bg-background p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold">{shipment.shipmentNo}</div>
          <div className="text-muted-foreground text-xs">{shipment.vendor}</div>
        </div>
        <Badge
          variant={status === "delayed" ? "destructive" : "secondary"}
          className="rounded-full"
        >
          {statusPill}
        </Badge>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div className="space-y-1">
          <div className="text-muted-foreground text-xs">Quantity</div>
          <div className="font-medium">
            {shipment.quantityKg.toLocaleString()} kg
          </div>
        </div>
        <div className="space-y-1">
          <div className="text-muted-foreground text-xs">ETA</div>
          <div className="font-medium">
            {shipment.etaISO
              ? new Date(shipment.etaISO).toLocaleDateString(undefined, {
                  month: "short",
                  day: "2-digit",
                  year: "numeric",
                })
              : "—"}
          </div>
        </div>
        <div className="space-y-1">
          <div className="text-muted-foreground text-xs">Time in transit</div>
          <div className="font-medium">
            {shipment.timeInTransitDays != null
              ? `${shipment.timeInTransitDays} days`
              : "—"}
          </div>
        </div>
        <div className="space-y-1">
          <div className="text-muted-foreground text-xs">Status</div>
          <div className="font-medium">{statusPill}</div>
        </div>
      </div>
    </div>
  );
}

function DemandDriverRow({ item }: { item: DemandDriverTimelineItem }) {
  const bg =
    item.impact === "negative"
      ? "bg-red-50 border-red-200"
      : item.impact === "positive"
        ? "bg-emerald-50 border-emerald-200"
        : "bg-amber-50 border-amber-200";

  const delta =
    item.deltaKg === 0
      ? "0 kg"
      : `${item.deltaKg > 0 ? "+" : ""}${item.deltaKg.toLocaleString()} kg`;

  return (
    <div className={cn("rounded-2xl border p-4", bg)}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold">{item.title}</div>
          <div className="text-muted-foreground text-xs">{item.weekLabel}</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="font-semibold text-sm">{delta}</div>
          <button
            type="button"
            className="text-muted-foreground text-xs underline-offset-2 hover:underline"
            onClick={() => {
              if (item.details) {
                window.alert(item.details);
              }
            }}
          >
            Details
          </button>
        </div>
      </div>
    </div>
  );
}
